import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const fallbackTestimonials = [
  {
    id: "1",
    client_name: "Sarah & James",
    client_title: "Wedding Clients",
    quote: "Prez Media captured our wedding day with such artistry. Every time we watch our film, we're transported back to those magical moments. Absolutely stunning work.",
    client_image_url: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=100&auto=format&fit=crop",
  },
  {
    id: "2",
    client_name: "Marcus Thompson",
    client_title: "Club Owner, Velvet Lounge",
    quote: "The promo videos have completely transformed how we market our venue. The energy they captured is incredible. Bookings have increased significantly.",
    client_image_url: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=100&auto=format&fit=crop",
  },
  {
    id: "3",
    client_name: "Emily Chen",
    client_title: "Real Estate Agent",
    quote: "Properties sell faster with their videos. The cinematic quality sets my listings apart from the competition. A game-changer for my business.",
    client_image_url: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=100&auto=format&fit=crop",
  },
  {
    id: "4",
    client_name: "David Park",
    client_title: "Brand Director, Luxe Fashion",
    quote: "Working with Prez Media elevated our brand to new heights. Their understanding of visual storytelling is exceptional. True artists.",
    client_image_url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=100&auto=format&fit=crop",
  },
];

export function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);

  const { data: cmsTestimonials } = useQuery({
    queryKey: ["cms-testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cms_testimonials")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  // Use CMS testimonials if available, otherwise fallback
  const testimonials = cmsTestimonials && cmsTestimonials.length > 0 
    ? cmsTestimonials 
    : fallbackTestimonials;

  const next = () => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % testimonials.length);
  };

  const prev = () => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  // Reset current index if it exceeds new testimonials length
  useEffect(() => {
    if (current >= testimonials.length) {
      setCurrent(0);
    }
  }, [testimonials.length, current]);

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 100 : -100,
      opacity: 0,
    }),
  };

  const currentTestimonial = testimonials[current];

  if (!currentTestimonial) return null;

  return (
    <section className="py-24 bg-card">
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary text-sm font-medium tracking-[0.3em] uppercase">
            Testimonials
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold mt-4">
            What Our Clients Say
          </h2>
        </motion.div>

        {/* Testimonial Carousel */}
        <div className="max-w-4xl mx-auto relative">
          <div className="overflow-hidden py-8">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentTestimonial.id}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="text-center"
              >
                <Quote className="w-12 h-12 text-primary/30 mx-auto mb-8" />
                
                <p className="text-xl md:text-2xl text-foreground leading-relaxed mb-8 font-light italic">
                  "{currentTestimonial.quote}"
                </p>
                
                <div className="flex items-center justify-center gap-4">
                  {currentTestimonial.client_image_url && (
                    <img
                      src={currentTestimonial.client_image_url}
                      alt={currentTestimonial.client_name}
                      width="56"
                      height="56"
                      loading="lazy"
                      decoding="async"
                      className="w-14 h-14 rounded-full object-cover border-2 border-primary/30"
                    />
                  )}
                  <div className="text-left">
                    <p className="font-serif font-semibold text-foreground">
                      {currentTestimonial.client_name}
                    </p>
                    {currentTestimonial.client_title && (
                      <p className="text-sm text-muted-foreground">
                        {currentTestimonial.client_title}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prev}
              className="w-12 h-12 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={20} />
            </button>
            
            <div className="flex gap-1">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setDirection(index > current ? 1 : -1);
                    setCurrent(index);
                  }}
                  className="min-w-6 h-6 flex items-center justify-center"
                  aria-label={`Go to testimonial ${index + 1}`}
                >
                  <span
                    className={`block h-2 rounded-full transition-all ${
                      index === current
                        ? "w-8 bg-primary"
                        : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                    }`}
                  />
                </button>
              ))}
            </div>
            
            <button
              onClick={next}
              className="w-12 h-12 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors"
              aria-label="Next testimonial"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
